import React from 'react'
import { Breadcrumbs as MuiBreadcrumbs, Typography, Link, Box, IconButton } from '@mui/material'
import { NavigateNext as NavigateNextIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useResponsive } from '../hooks/useResponsive'

/**
 * Breadcrumb navigation for deep pages (3+ route segments).
 *
 * - **Desktop/laptop** (≥ 1200px): Full trail with all crumbs linked
 * - **Mobile/tablet** (< 1200px): Compact mode — back arrow + parent link + current page
 *
 * Returns `null` when breadcrumbs are not applicable (top-level/depth-2 pages).
 * All crumb links pass `env` via navigation state for multi-tenant routing.
 */
const Breadcrumb: React.FC = () => {
  const { crumbs, visible } = useBreadcrumbs()
  const { showPermanentDrawer } = useResponsive()
  const navigate = useNavigate()
  const { t } = useTranslation()

  if (!visible || crumbs.length === 0) return null

  const isDesktopMode = showPermanentDrawer // ≥ 1200px

  // Mobile/tablet: show back arrow + parent + current
  if (!isDesktopMode) {
    const currentCrumb = crumbs[crumbs.length - 1]
    // Find the nearest parent with a path (for the back arrow)
    const parentCrumb = [...crumbs].reverse().find(c => !c.isCurrentPage && c.path)

    return (
      <Box
        component='nav'
        aria-label={t('breadcrumb.ariaLabel')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1.5,
        }}>
        {parentCrumb && (
          <IconButton
            size='small'
            onClick={() => navigate(parentCrumb.path, { state: parentCrumb.navigationState })}
            aria-label={parentCrumb.label}
            sx={{ minWidth: 44, minHeight: 44 }}>
            <ArrowBackIcon fontSize='small' />
          </IconButton>
        )}
        {parentCrumb && (
          <Link
            component='button'
            variant='body2'
            underline='hover'
            color='primary'
            onClick={() => navigate(parentCrumb.path, { state: parentCrumb.navigationState })}
            sx={{
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}>
            {parentCrumb.label}
          </Link>
        )}
        {parentCrumb && (
          <NavigateNextIcon sx={{ fontSize: '1.2rem', color: 'text.disabled', flexShrink: 0 }} />
        )}
        <Typography
          variant='body2'
          noWrap
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            maxWidth: 200
          }}>
          {currentCrumb.label}
        </Typography>
      </Box>
    );
  }

  // Desktop/laptop: full breadcrumb trail
  return (
    <Box component='nav' aria-label={t('breadcrumb.ariaLabel')} sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1.2rem' }} />}
        aria-label={t('breadcrumb.ariaLabel')}>
        {crumbs.map((crumb, index) => {
          if (crumb.isCurrentPage) {
            return (
              <Typography
                key={index}
                variant='body2'
                sx={{
                  color: 'text.primary',
                  fontWeight: 600
                }}>
                {crumb.label}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              component='button'
              variant='body2'
              underline='hover'
              color='primary'
              onClick={() => navigate(crumb.path, { state: crumb.navigationState })}
              sx={{ cursor: 'pointer' }}>
              {crumb.label}
            </Link>
          )
        })}
      </MuiBreadcrumbs>
    </Box>
  );
}

export default Breadcrumb
